import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { FormattedMessage } from "react-intl";
import { useAuth, useLinks, useModalConfirmation, useToast } from "hooks";
import {
  GENERATE_USER_RECOVERY_OTP,
  RESEND_PLATFORM_INVITE,
  SET_FEATURE_ENTITLEMENTS,
  UPDATE_USER,
  UPDATE_USER_OTP_CONFIGURATION,
} from "graphql/mutations";
import { GET_PLATFORM_ACCESS_USERS } from "graphql/queries";
import { USER_TYPES } from "constants";
import { ICON_TYPE } from "components/Icon";
import { USER_TRANSITIONS } from "constants/index";

export default function useAccessUsers() {
  const { toast } = useToast();
  const me = useAuth();
  const { show } = useModalConfirmation();
  const keyName = "platformAccessUsers";
  const [updateUser, { loading: updating }] = useMutation(UPDATE_USER);
  const [updateUserOtpConfiguration, { loading: updatingOtpConfiguration }] =
    useMutation(UPDATE_USER_OTP_CONFIGURATION);
  const [generateUserRecoveryOtp, { loading: generatingRecoveryOtp }] = useMutation(GENERATE_USER_RECOVERY_OTP);
  const [resendPlatformInvite, { loading: resending }] = useMutation(RESEND_PLATFORM_INVITE);
  const [setFeatureEntitlements, { loading: settingFeatureEntitlements }] = useMutation(SET_FEATURE_ENTITLEMENTS, {
    refetchQueries: [GET_PLATFORM_ACCESS_USERS],
  });
  const [selectedUser, setSelectedUser] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [showFeatureEntitlementsForm, setShowFeatureEntitlementsForm] = useState(false);
  const links = useLinks();

  /**
   * @description Handle the form update
   *
   * @param {String}  id
   * @param {Object}  params
   * @param {String}  messageId
   */
  const handleUpdateUser = (id, params, messageId = "Edit") => {
    updateUser({
      variables: {
        input: {
          id,
          params,
        },
      },
      onCompleted: () => toast.success(<FormattedMessage id={`useAccessUsers.${messageId}Success`} />),
      onError: () => toast.error(<FormattedMessage id={`useAccessUsers.${messageId}Error`} />),
    });
  };

  /**
   * @description Handle the portal feature entitlements update
   *
   * @param {String}    id
   * @param {Array}     features
   * @param {Function}  onSuccess
   */
  const handleSetFeatureEntitlements = (id, features, onSuccess) => {
    setFeatureEntitlements({
      variables: {
        input: {
          id,
          params: { features },
        },
      },
      onCompleted: () => {
        onSuccess?.();
        toast.success(<FormattedMessage id="useAccessUsers.FeatureEntitlementsSuccess" />);
      },
      onError: () => toast.error(<FormattedMessage id="useAccessUsers.FeatureEntitlementsError" />),
    });
  };

  /**
   * @description Handle the resend invite action
   *
   * @param {String} id
   */
  const handleResendInvite = (id) => {
    resendPlatformInvite({
      variables: {
        input: { id },
      },
      onCompleted: () => toast.success(<FormattedMessage id={`useAccessUsers.ResendSuccess`} />),
      onError: () => toast.error(<FormattedMessage id={`useAccessUsers.ResendError`} />),
    });
  };

  /**
   * @description Handle the OTP enabled toggle action
   *
   * @param {Object} user
   */
  const handleUpdateUserOtpConfiguration = async ({ id, name, otpEnabled }) => {
    const nextOtpEnabled = !otpEnabled;
    const action = nextOtpEnabled ? "Enable" : "Disable";
    const response = await show(
      <FormattedMessage id={`Settings.Users.UserRowAdvancedOptions.Otp.${action}.Title`} />,
      <FormattedMessage
        id={`Settings.Users.UserRowAdvancedOptions.Otp.${action}.Confirmation`}
        values={{ name: <b>{name}</b> }}
      />,
      {
        showCancel: true,
        showSave: nextOtpEnabled,
        showSaveAsDestructable: !nextOtpEnabled,
        primaryButtonMessageId: `Settings.Users.UserRowAdvancedOptions.Otp.${action}.Cta`,
      }
    );

    if (response) {
      updateUserOtpConfiguration({
        variables: {
          input: { id, otpEnabled: nextOtpEnabled },
        },
        onCompleted: () => toast.success(<FormattedMessage id={`useAccessUsers.Otp${action}Success`} />),
        onError: () => toast.error(<FormattedMessage id={`useAccessUsers.Otp${action}Error`} />),
      });
    }
  };

  /**
   * @description Generate a one-time recovery OTP and copy it to the clipboard
   *
   * @param {Object} user
   */
  const handleGenerateUserRecoveryOtp = async ({ id, name }) => {
    const response = await show(
      <FormattedMessage id="Settings.Users.UserRowAdvancedOptions.Otp.Recovery.Title" />,
      <FormattedMessage
        id="Settings.Users.UserRowAdvancedOptions.Otp.Recovery.Confirmation"
        values={{ name: <b>{name}</b> }}
      />,
      {
        showCancel: true,
        showSave: true,
        primaryButtonMessageId: "Settings.Users.UserRowAdvancedOptions.Otp.Recovery.Cta",
      }
    );

    if (response) {
      generateUserRecoveryOtp({
        variables: {
          input: { id },
        },
        onCompleted: (data) => {
          const otp = data?.generateUserRecoveryOtp?.otp;

          if (otp) {
            window.navigator.clipboard.writeText(otp);
          }

          toast.success(<FormattedMessage id="useAccessUsers.RecoveryOtpSuccess" />);
        },
        onError: () => toast.error(<FormattedMessage id="useAccessUsers.RecoveryOtpError" />),
      });
    }
  };

  /**
   * @description Get the advanced options for each row based on user type
   *
   * @param {Object} row
   */
  const getAdvancedOptions = (row) => {
    const { id, uuid, name, deactivated, otpEnabled, signInCount, selectedTab, role } = row;
    const showcaseUserType = USER_TYPES.showcase.toLowerCase();
    const signInCountIsZero = signInCount === 0;
    const isShowcaseUser = [selectedTab, role].includes(showcaseUserType);
    const isAdminUser = role === USER_TYPES.admin.toLowerCase();

    const EDIT_USER_OPTION = {
      label: <FormattedMessage id={"Settings.Users.UserRowAdvancedOptions.Edit"} />,
      icon: ICON_TYPE.edit,
      onClick: () => {
        setSelectedUser({ ...row, type: row?.role });
        setShowForm(true);
      },
      disabled: id === me?.id,
    };

    const ACTIVATE_OPTION = {
      label: (
        <FormattedMessage id={`Settings.Users.UserRowAdvancedOptions.${deactivated ? `Activate` : `Deactivate`}`} />
      ),
      icon: deactivated ? ICON_TYPE.checkDotted : ICON_TYPE.close,
      onClick: () => handleUpdateUser(id, { deactivated: !deactivated }),
      disabled: id === me?.id,
    };

    const RE_SEND_OPTION = {
      label: <FormattedMessage id={"Settings.Users.UserRowAdvancedOptions.Resend"} />,
      icon: ICON_TYPE.checkDotted,
      onClick: () => handleResendInvite(id),
      disabled: deactivated,
    };

    const BROWSE_AS_OPTION = {
      label: <FormattedMessage id={"Settings.Users.UserRowAdvancedOptions.BrowseAs"} values={{ name: name }} />,
      icon: ICON_TYPE.user,
      onClick: () => window.open(links.start_impersonating(id), "_self"),
      disabled: deactivated,
    };

    const OTP_CONFIGURATION_OPTION = {
      label: <FormattedMessage id={`Settings.Users.UserRowAdvancedOptions.Otp.${otpEnabled ? "Disable" : "Enable"}`} />,
      icon: otpEnabled ? ICON_TYPE.unavailable : ICON_TYPE.checkDotted,
      onClick: () => handleUpdateUserOtpConfiguration(row),
      disabled: deactivated,
    };

    const RECOVERY_OTP_OPTION = {
      label: <FormattedMessage id="Settings.Users.UserRowAdvancedOptions.Otp.Recovery" />,
      icon: ICON_TYPE.copy,
      onClick: () => handleGenerateUserRecoveryOtp(row),
      disabled: deactivated,
    };

    const MAGIC_LINK_OPTION = {
      label: <FormattedMessage id="Settings.Users.UserRowAdvancedOptions.ShowcaseLink" />,
      icon: ICON_TYPE.copy,
      onClick: async () => {
        const link = `${window.location.origin}${links.showcase_magic_link(uuid)}`;

        await window.navigator.clipboard.writeText(link);
        toast.success(<FormattedMessage id="Missions.MissionPublicLink.LinkCopied" />);
      },
      disabled: deactivated,
    };

    const CONVERT_TO_CLIENT_OPTION = {
      label: <FormattedMessage id="Settings.Users.UserRowAdvancedOptions.ConvertToClient" />,
      icon: ICON_TYPE.user,
      onClick: () => {
        setSelectedUser({ ...row, type: USER_TRANSITIONS.showcaseToClient });
        setShowForm(true);
      },
    };

    let options = [ACTIVATE_OPTION, BROWSE_AS_OPTION, EDIT_USER_OPTION];

    if (isShowcaseUser) {
      options.push(MAGIC_LINK_OPTION, CONVERT_TO_CLIENT_OPTION);
    } else {
      if (!isAdminUser || !otpEnabled) {
        options.push(OTP_CONFIGURATION_OPTION);
      }

      if (otpEnabled) {
        options.push(RECOVERY_OTP_OPTION);
      }

      if (signInCountIsZero) {
        options.push(RE_SEND_OPTION);
      }
    }

    return options;
  };

  return {
    selectedUser,
    showForm,
    setShowForm,
    showFeatureEntitlementsForm,
    setShowFeatureEntitlementsForm,
    handleUpdateUser,
    handleSetFeatureEntitlements,
    settingFeatureEntitlements,
    getAdvancedOptions,
    loading: updating || updatingOtpConfiguration || generatingRecoveryOtp || resending,
    keyName,
  };
}
